import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { BsChevronLeft, BsX, BsCheckLg, BsChevronDown } from 'react-icons/bs';
import classNames from 'classnames';
import firebase from '@/config/firebase.config';
import { authActionTypes } from '@store/Auth/Auth.actionTypes';
import { useSetupGuide } from '@/contexts/SetupGuide.context';
import { useDefaultDashboardRoute } from '@/hooks/usePermission';
import styles from './SetupGuide.module.scss';

const GUIDE_ITEMS = [
  {
    id: 1,
    title: 'Invite your team',
    description:
      "Invite team members to Prodaff. They'll get an email to join your workspace and get started.",
    ctaLabel: 'Show me how',
  },
  {
    id: 2,
    title: 'Download & install the desktop app',
    description:
      'Employees install the lightweight Prodaff app on their workstations. It runs quietly in the background and starts tracking automatically.',
    ctaLabel: 'Show me how',
  },
  {
    id: 3,
    title: 'Explore your productivity data',
    description:
      'Once your team is set up, head to the dashboard to see real-time activity, app usage, and productivity insights.',
    ctaLabel: 'Take a tour',
  },
];

const STEP_MINUTES = [2, 3, 1];

const DEMO_BASE_URL = 'https://demo.prodaff.com';

const ITEM_TOUR_MAP = {
  1: 'add-employee',
  2: 'download-desktop-app',
  3: 'productivity-data',
};

const COMPLETED_PARAM_MAP = {
  'add-employee': 1,
  'download-desktop-app': 2,
  'productivity-data': 3,
};

const getRemainingMinutes = (completedCount) =>
  STEP_MINUTES.slice(completedCount).reduce((sum, m) => sum + m, 0);

const db = firebase.firestore();

export const SetupGuide = () => {
  const { isOpen, close, open } = useSetupGuide();
  const history = useHistory();
  const dispatch = useDispatch();
  const defaultDashboardRoute = useDefaultDashboardRoute();

  const userId = useSelector((state) => state.auth?.profile?.id);
  const setupGuideData = useSelector(
    (state) => state.auth?.profile?.setupGuideData
  );

  const [manuallyCompleted, setManuallyCompleted] = useState(new Set());
  const [expandedItem, setExpandedItem] = useState(null);
  const initializedRef = useRef(false);

  // Sync from Firestore-backed Redux on profile load (once)
  useEffect(() => {
    if (initializedRef.current) return;
    if (!userId) return;
    initializedRef.current = true;
    const completed = new Set(setupGuideData?.completedSteps ?? []);
    setManuallyCompleted(completed);
    const first = GUIDE_ITEMS.find((item) => !completed.has(item.id));
    setExpandedItem(first ? first.id : null);
  }, [userId, setupGuideData]);

  const persistStep = (updatedSet) => {
    if (!userId) return;
    const isAllDone = updatedSet.size === GUIDE_ITEMS.length;
    const patch = {
      'setupGuideData.completedSteps': firebase.firestore.FieldValue.arrayUnion(
        ...[...updatedSet]
      ),
      ...(isAllDone ? { 'setupGuideData.dismissed': true } : {}),
    };
    db.collection('membersDatabase')
      .doc(userId)
      .update(patch)
      .catch(() => {});
    dispatch({
      type: authActionTypes.PATCH_PROFILE,
      payload: {
        setupGuideData: {
          ...(setupGuideData ?? {}),
          completedSteps: [...updatedSet],
          ...(isAllDone ? { dismissed: true } : {}),
        },
      },
    });
  };

  // Handle return from demo (?completed=add-employee in URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const completedParam = params.get('completed');
    const itemId = COMPLETED_PARAM_MAP[completedParam];
    if (!itemId) return;

    params.delete('completed');
    history.replace({
      pathname: window.location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    });

    setManuallyCompleted((prev) => {
      const updated = new Set([...prev, itemId]);
      persistStep(updated);
      const next = GUIDE_ITEMS.find(
        (item) => !updated.has(item.id) && item.id > itemId
      );
      setExpandedItem(next ? next.id : null);
      return updated;
    });

    open();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCta = (itemId) => {
    const tourName = ITEM_TOUR_MAP[itemId];
    if (!tourName) return;
    const callbackUrl = `${window.location.origin}${defaultDashboardRoute}?completed=${tourName}`;
    window.location.href = `${DEMO_BASE_URL}/tour/${tourName}?callback=${encodeURIComponent(callbackUrl)}`;
  };

  const handleMarkCompleted = (itemId) => {
    const updated = new Set([...manuallyCompleted, itemId]);
    setManuallyCompleted(updated);
    persistStep(updated);
    const next = GUIDE_ITEMS.find(
      (item) => !updated.has(item.id) && item.id > itemId
    );
    setExpandedItem(next ? next.id : null);
  };

  const handleItemToggle = (itemId) => {
    setExpandedItem((prev) => (prev === itemId ? null : itemId));
  };

  const isCompleted = (itemId) => manuallyCompleted.has(itemId);
  const completedCount = manuallyCompleted.size;
  const totalItems = GUIDE_ITEMS.length;
  const progressPercent = Math.min((completedCount / totalItems) * 100, 100);
  const remainingMinutes = getRemainingMinutes(completedCount);

  if (!isOpen) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.panelNav}>
        <button className={styles.navBtn} onClick={close} aria-label="Go back">
          <BsChevronLeft size={15} />
        </button>
        <button className={styles.navBtn} onClick={close} aria-label="Close">
          <BsX size={20} />
        </button>
      </div>

      <div className={styles.scrollableContent}>
        <div className={styles.welcome}>
          <h2 className={styles.welcomeTitle}>Welcome to Prodaff! 🎉</h2>
          <p className={styles.welcomeSubtitle}>
            A few quick tips to help you get the most out of Prodaff
          </p>
          <div className={styles.authorRow}>
            <div className={styles.authorAvatar}>P</div>
            <span className={styles.authorName}>Team at Prodaff</span>
          </div>
        </div>

        <div className={styles.progressSection}>
          <div className={styles.progressMeta}>
            <span className={styles.progressDone}>
              {completedCount} of {totalItems} done
            </span>
            {remainingMinutes > 0 && (
              <span className={styles.progressTime}>
                About {remainingMinutes} minutes left
              </span>
            )}
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className={styles.checklist}>
          {GUIDE_ITEMS.map((item) => {
            const completed = isCompleted(item.id);
            const isExpanded = expandedItem === item.id;

            return (
              <div
                key={item.id}
                className={classNames(styles.item, {
                  [styles.item_completed]: completed,
                  [styles.item_expanded]: isExpanded,
                })}
              >
                <button
                  className={styles.itemHeader}
                  onClick={() => handleItemToggle(item.id)}
                >
                  <div
                    className={classNames(styles.itemIndicator, {
                      [styles.itemIndicator_completed]: completed,
                    })}
                  >
                    {completed ? (
                      <BsCheckLg size={11} />
                    ) : (
                      <span>{item.id}</span>
                    )}
                  </div>
                  <span
                    className={classNames(styles.itemTitle, {
                      [styles.itemTitle_bold]: isExpanded,
                    })}
                  >
                    {item.title}
                  </span>
                  <BsChevronDown
                    size={14}
                    className={classNames(styles.chevron, {
                      [styles.chevron_rotated]: isExpanded,
                    })}
                  />
                </button>

                {isExpanded && (
                  <div className={styles.itemBody}>
                    <p className={styles.itemDescription}>{item.description}</p>
                    <div className={styles.itemActions}>
                      {item.ctaLabel && (
                        <button
                          className={styles.ctaButton}
                          onClick={() => handleCta(item.id)}
                        >
                          {item.ctaLabel}
                        </button>
                      )}
                      {!completed && (
                        <button
                          className={styles.markCompletedBtn}
                          onClick={() => handleMarkCompleted(item.id)}
                        >
                          <BsCheckLg size={11} />
                          Mark as completed
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
